/**
 * TemplateCustomizationModal Component
 * 
 * Allows users to customize a template before creating a trip:
 * - Select start date
 * - Optionally override trip name
 * - Optionally adjust budget
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import {
  View,
  Modal,
  ScrollView,
  TextInput,
  Pressable,
  ActivityIndicator,
  InteractionManager,
} from 'react-native';
import { Calendar, type DateData } from 'react-native-calendars';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { ThemedText, SubHeading, Body, Caption } from '../ui/Text';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { SuccessAnimation } from '../ui/LoadingState';
import type { TripTemplate, TemplateCustomization } from '@/types/tripTemplates';
import {
  isPremiumGatePresentedError,
  TEMPLATE_CREATE_IN_PROGRESS_REASON,
} from '@/services/entitlements';

function coerceBudgetAmount(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

type TemplateCustomizationModalProps = {
  visible: boolean;
  template: TripTemplate | null;
  onClose: (reason?: 'user' | 'success' | 'premium_gate') => void;
  onDismiss?: () => void;
  onConfirm: (customization: TemplateCustomization) => Promise<void>;
  embedded?: boolean;
};

export function TemplateCustomizationModal({
  visible,
  template,
  onClose,
  onDismiss,
  onConfirm,
  embedded = false,
}: TemplateCustomizationModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [customName, setCustomName] = useState('');
  const [customBudget, setCustomBudget] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    // Initialize to first day of current month to avoid timezone issues
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creationProgress, setCreationProgress] = useState<{
    stage: string;
    progress: number;
  } | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const availableYears = useMemo(() => {
    const baseYear = new Date().getFullYear();
    // Include past 2 years and future 20 years for better range
    return Array.from({ length: 23 }, (_, index) => baseYear - 2 + index);
  }, []);

  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  // When calendar opens, ensure it shows the correct month (today or selected date)
  // This must be before the early return to follow Rules of Hooks
  useEffect(() => {
    if (showCalendar && template) {
      if (startDate) {
        // If a date is already selected, show that month
        const dateMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        setCalendarMonth(dateMonth);
        console.log('[TemplateCustomization] Calendar opened - setting month to selected date:', {
          selectedDate: formatDate(startDate),
          targetMonth: dateMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        });
      } else {
        // Otherwise, show today's month
        const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setCalendarMonth(todayMonth);
        console.log('[TemplateCustomization] Calendar opened - setting month to today:', {
          today: formatDate(today),
          targetMonth: todayMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        });
      }
    }
  }, [showCalendar, startDate, today, template]);

  // Reset state when template changes
  const handleModalOpen = () => {
    if (visible) {
      setStartDate(null);
      setCustomName('');
      setCustomBudget('');
      setShowCalendar(false);
      setShowYearPicker(false);
      // Reset calendar month to first day of current month
      const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      setCalendarMonth(todayMonth);
      setError('');
      setLoading(false);
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format date as YYYY-MM-DD in local timezone (avoiding UTC conversion issues)
  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date string for react-native-calendars current prop
  // Use the 15th of the month to avoid timezone edge cases at month boundaries
  const formatCalendarCurrent = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    // Use day 15 to avoid timezone issues at month boundaries
    return `${year}-${month}-15`;
  };

  const calculateEndDate = (start: Date): Date => {
    if (!template) return start;
    const end = new Date(start);
    end.setDate(end.getDate() + template.duration - 1);
    return end;
  };

  const handleDateSelect = (day: DateData) => {
    // Use dateString to avoid timezone issues - it's already in YYYY-MM-DD format
    // Parse it directly to ensure we get the correct local date
    // dateString format: "YYYY-MM-DD" (e.g., "2024-06-15")
    // IMPORTANT: react-native-calendars dateString is in local date format, not UTC
    const dateParts = day.dateString.split('-');
    if (dateParts.length !== 3) {
      console.error('[TemplateCustomization] Invalid dateString format:', day.dateString);
      return;
    }
    
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10); // 1-12 (1-indexed from string)
    const dayOfMonth = parseInt(dateParts[2], 10);
    
    // Validate parsed values
    if (isNaN(year) || isNaN(month) || isNaN(dayOfMonth) || month < 1 || month > 12 || dayOfMonth < 1 || dayOfMonth > 31) {
      console.error('[TemplateCustomization] Invalid date values:', { year, month, dayOfMonth, dateString: day.dateString });
      return;
    }
    
    // Create date directly in local timezone using the Date constructor
    // JavaScript Date constructor uses 0-indexed months (0 = January, 11 = December)
    // Create at local noon to avoid any timezone edge cases, then set to midnight
    const selected = new Date(year, month - 1, dayOfMonth, 12, 0, 0, 0);
    selected.setHours(0, 0, 0, 0);
    
    // Verify the date was created correctly
    const createdYear = selected.getFullYear();
    const createdMonth = selected.getMonth() + 1; // Convert back to 1-indexed for comparison
    const createdDay = selected.getDate();
    
    // Log for debugging
    console.log('[TemplateCustomization] Date selected:', {
      dateString: day.dateString,
      parsed: { year, month, dayOfMonth },
      created: { year: createdYear, month: createdMonth, day: createdDay },
      formatted: formatDate(selected),
    });
    
    // Set the selected date
    setStartDate(selected);
    
    // Update calendar month to show the selected date's month
    const newMonth = new Date(year, month - 1, 1);
    setCalendarMonth(newMonth);
    
    setShowCalendar(false);
  };

  const handleMonthShift = (offset: number) => {
    setCalendarMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + offset);
      return next;
    });
  };

  const handleYearSelect = (year: number) => {
    setCalendarMonth((prev) => {
      const next = new Date(prev);
      next.setFullYear(year);
      // Ensure the month stays valid (e.g., if we were on Feb 29 and switched to non-leap year)
      const daysInNewMonth = new Date(year, next.getMonth() + 1, 0).getDate();
      if (next.getDate() > daysInNewMonth) {
        next.setDate(daysInNewMonth);
      }
      return next;
    });
    setShowYearPicker(false);
  };

  const handleConfirm = async () => {
    // Validation
    if (!startDate) {
      setError('Please select a start date');
      return;
    }

    // Check if start date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (startDate < today) {
      setError('Start date cannot be in the past');
      return;
    }

    // Validate custom budget if provided
    const budgetValue = customBudget ? parseInt(customBudget, 10) : undefined;
    if (customBudget && (isNaN(budgetValue!) || budgetValue! <= 0)) {
      setError('Please enter a valid budget amount');
      return;
    }

    setError('');
    setLoading(true);
    setCreationProgress({ stage: 'Preparing trip details...', progress: 10 });

    try {
      const customization: TemplateCustomization = {
        startDate,
        name: customName.trim() || undefined,
        budget: budgetValue,
      };

      // Simulate progress stages (the actual service is fast, but this gives better UX)
      setCreationProgress({ stage: 'Creating trip...', progress: 30 });
      
      // Small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 300));
      setCreationProgress({ stage: 'Building itinerary...', progress: 60 });
      
      await onConfirm(customization);
      
      setCreationProgress({ stage: 'Finalizing...', progress: 90 });
      await new Promise(resolve => setTimeout(resolve, 200));
      setCreationProgress({ stage: 'Complete!', progress: 100 });
      
      // Small delay to show 100% progress
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Show success animation - the onComplete handler will close the modal
      setLoading(false);
      setShowSuccess(true);
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      // Fallback: ensure modal closes even if animation is interrupted (e.g. app switch).
      // Note: Must be longer than SuccessAnimation's 2000ms timeout to avoid race condition
      successTimeoutRef.current = setTimeout(() => {
        successTimeoutRef.current = null;
        setShowSuccess(false);
        setCreationProgress(null);
        onClose('success');
      }, 2500);
    } catch (err) {
      if (isPremiumGatePresentedError(err)) {
        setCreationProgress(null);
        setLoading(false);
        setShowSuccess(false);
        // Dismiss this sheet so the trips screen is not left blocked under the premium gate
        // (browser flow: parent reopens the template browser via onDismiss).
        if (err.message !== TEMPLATE_CREATE_IN_PROGRESS_REASON) {
          InteractionManager.runAfterInteractions(() => {
            onClose(err.message.includes('trip_limit') ? 'premium_gate' : 'user');
          });
        }
        return;
      }
      console.error('[TemplateCustomization] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create trip');
      setCreationProgress(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, []);

  if (!template) return null;

  const templateBudgetMin = coerceBudgetAmount(template.estimatedBudget?.min);
  const templateBudgetMax = coerceBudgetAmount(template.estimatedBudget?.max);
  const templateBudgetMid = Math.round((templateBudgetMin + templateBudgetMax) / 2);

  const minDate = formatDateString(today);

  // Calculate end date for display
  const endDate = startDate ? calculateEndDate(startDate) : null;

  const content = (
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        {/* Pinned header (was inside ScrollView and slid under status bar when scrolling) */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            paddingTop: Math.max(insets.top, theme.spacing(2)) + theme.spacing(1),
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.borderMuted,
            backgroundColor: theme.colors.background,
          }}
        >
          <SubHeading>Customize Your Trip</SubHeading>
          <Pressable onPress={() => onClose('user')} disabled={loading}>
            <Ionicons name="close" size={24} color={theme.colors.text} />
          </Pressable>
        </View>

        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            paddingBottom: theme.spacing(2),
          }}
        >
          {/* Content */}
          <View style={{ padding: theme.spacing(2), gap: theme.spacing(2.5) }}>
            {/* Template Summary */}
            <Card variant="outlined">
              <View style={{ gap: theme.spacing(1) }}>
                <Body style={{ fontWeight: '600' }}>{template.title}</Body>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(2) }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    <Ionicons name="location-outline" size={14} color={theme.colors.textSecondary} />
                    <Caption color={theme.colors.textSecondary}>{template.destination}</Caption>
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    <Ionicons name="calendar-outline" size={14} color={theme.colors.textSecondary} />
                    <Caption color={theme.colors.textSecondary}>{template.duration} days</Caption>
                  </View>
                </View>
              </View>
            </Card>

            {/* Start Date Selection */}
            <View>
              <Body style={{ marginBottom: theme.spacing(0.5), fontWeight: '600' }}>
                When do you want to start? <Caption color={theme.colors.error}>*</Caption>
              </Body>
              
              {/* Best Months Hint - Always visible to help users choose */}
              {template.bestMonths && template.bestMonths.length > 0 && (
                <View 
                  style={{ 
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    gap: theme.spacing(0.75),
                    marginBottom: theme.spacing(1),
                    paddingVertical: theme.spacing(0.75),
                    paddingHorizontal: theme.spacing(1),
                    backgroundColor: `${theme.colors.primary}08`,
                    borderRadius: theme.radii.md,
                    borderLeftWidth: 3,
                    borderLeftColor: theme.colors.primary,
                  }}
                >
                  <Ionicons name="sunny-outline" size={16} color={theme.colors.primary} />
                  <Caption color={theme.colors.primary} style={{ flex: 1 }}>
                    <Caption style={{ fontWeight: '600', color: theme.colors.primary }}>Best time to visit:</Caption>{' '}
                    {template.bestMonths.join(', ')}
                  </Caption>
                </View>
              )}
              
              <Pressable
                onPress={() => setShowCalendar(!showCalendar)}
                disabled={loading}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: theme.spacing(1.5),
                  borderRadius: theme.radii.lg,
                  borderWidth: 1,
                  borderColor: error && !startDate ? theme.colors.error : theme.colors.borderMuted,
                  backgroundColor: theme.colors.card,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons
                    name="calendar-outline"
                    size={20}
                    color={startDate ? theme.colors.text : theme.colors.textSecondary}
                  />
                  <Body color={startDate ? theme.colors.text : theme.colors.textSecondary}>
                    {startDate ? formatDate(startDate) : 'Select start date'}
                  </Body>
                </View>
                <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
              </Pressable>

              {/* Calendar */}
              {showCalendar && (
                <Card variant="outlined" style={{ marginTop: theme.spacing(1) }}>
                  <View style={{ padding: theme.spacing(1) }}>
                  <Calendar
                    key={`calendar-${calendarMonth.getFullYear()}-${calendarMonth.getMonth()}-${startDate ? formatDateString(startDate) : 'no-date'}`}
                    onDayPress={handleDateSelect}
                    minDate={minDate}
                    enableSwipeMonths={true}
                    markedDates={
                      startDate
                        ? {
                            [formatDateString(startDate)]: {
                              selected: true,
                              selectedColor: theme.colors.primary,
                            },
                          }
                        : {}
                    }
                      current={formatCalendarCurrent(calendarMonth)}
                      onMonthChange={(month) => {
                        // react-native-calendars uses 1-indexed months
                        // Only update if the change came from user interaction, not from our programmatic update
                        const next = new Date(month.year, month.month - 1, 1);
                        // Check if this is different from our current calendarMonth to avoid loops
                        if (next.getTime() !== calendarMonth.getTime()) {
                          setCalendarMonth(next);
                        }
                      }}
                      renderHeader={(date) => {
                        const headerDate = new Date(date);
                        const label = headerDate.toLocaleDateString(undefined, {
                          month: 'long',
                          year: 'numeric',
                        });
                        return (
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              paddingHorizontal: theme.spacing(1),
                              paddingBottom: theme.spacing(1),
                            }}
                          >
                            <Pressable
                              onPress={() => setShowYearPicker(true)}
                              style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}
                            >
                              <Body style={{ fontWeight: '600' }}>{label}</Body>
                              <Ionicons name="chevron-down" size={16} color={theme.colors.text} />
                            </Pressable>
                          </View>
                        );
                      }}
                    theme={{
                        backgroundColor: theme.colors.card,
                        calendarBackground: theme.colors.card,
                        textSectionTitleColor: theme.colors.textSecondary,
                        monthTextColor: theme.colors.text,
                        dayTextColor: theme.colors.text,
                        todayTextColor: theme.colors.primary,
                        selectedDayBackgroundColor: theme.colors.primary,
                        selectedDayTextColor: theme.colors.onPrimary,
                        arrowColor: theme.colors.primary,
                        textDisabledColor: theme.colors.textSecondary + '55',
                      }}
                    />
                  </View>
                </Card>
              )}

              {/* End Date Display */}
              {endDate && (
                <View style={{ marginTop: theme.spacing(1) }}>
                  <Caption color={theme.colors.textSecondary}>
                    Trip ends on {formatDate(endDate)}
                  </Caption>
                </View>
              )}
            </View>

            {/* Trip Name (Optional) */}
            <View>
              <Body style={{ marginBottom: theme.spacing(1), fontWeight: '600' }}>
                Trip Name <Caption color={theme.colors.textSecondary}>(optional)</Caption>
              </Body>
              <TextInput
                value={customName}
                onChangeText={setCustomName}
                placeholder={template.title}
                placeholderTextColor={theme.colors.textSecondary}
                editable={!loading}
                style={{
                  padding: theme.spacing(1.5),
                  borderRadius: theme.radii.lg,
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  backgroundColor: theme.colors.card,
                  fontSize: 15,
                  color: theme.colors.text,
                }}
              />
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                Leave blank to use template name
              </Caption>
            </View>

            {/* Budget (Optional) */}
            <View>
              <Body style={{ marginBottom: theme.spacing(1), fontWeight: '600' }}>
                Budget <Caption color={theme.colors.textSecondary}>(optional)</Caption>
              </Body>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: theme.spacing(1.5),
                  borderRadius: theme.radii.lg,
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  backgroundColor: theme.colors.card,
                }}
              >
                <Body style={{ marginRight: theme.spacing(0.5) }}>$</Body>
                <TextInput
                  value={customBudget}
                  onChangeText={setCustomBudget}
                  placeholder={`${templateBudgetMid}`}
                  placeholderTextColor={theme.colors.textSecondary}
                  keyboardType="numeric"
                  editable={!loading}
                  style={{
                    flex: 1,
                    padding: theme.spacing(1.5),
                    fontSize: 15,
                    color: theme.colors.text,
                  }}
                />
              </View>
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                Template estimate:{' '}
                {templateBudgetMin === 0 && templateBudgetMax === 0
                  ? 'Not set'
                  : `$${templateBudgetMin.toLocaleString()}–$${templateBudgetMax.toLocaleString()}`}
              </Caption>
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                Costs will vary depending on where you're traveling from. You can also adjust this trip after creating it by deleting, editing, or adding items.
              </Caption>
            </View>

            {/* Date Selection Feedback */}
            {template.bestMonths && template.bestMonths.length > 0 && startDate && (() => {
              const selectedMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
              const isRecommendedMonth = template.bestMonths.includes(selectedMonth);
              
              return isRecommendedMonth ? (
                <Card variant="outlined" style={{ backgroundColor: `${theme.colors.success}15`, borderColor: `${theme.colors.success}30` }}>
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1), alignItems: 'center' }}>
                    <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                    <View style={{ flex: 1 }}>
                      <Caption color={theme.colors.success} style={{ fontWeight: '600' }}>
                        Great choice! {selectedMonth} is one of the best times to visit.
                      </Caption>
                    </View>
                  </View>
                </Card>
              ) : (
                <Card variant="outlined" style={{ backgroundColor: `${theme.colors.warning}10`, borderColor: `${theme.colors.warning}30` }}>
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1), alignItems: 'flex-start' }}>
                    <Ionicons name="alert-circle-outline" size={20} color={theme.colors.warning} />
                    <View style={{ flex: 1 }}>
                      <Caption color={theme.colors.warning} style={{ fontWeight: '600' }}>
                        Heads up: {selectedMonth} isn't typically the best time for this trip.
                      </Caption>
                      <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                        Consider visiting in {template.bestMonths.slice(0, 3).join(', ')} for better weather and conditions.
                      </Caption>
                    </View>
                  </View>
                </Card>
              );
            })()}

            {/* Tickets Reminder */}
            <Card variant="outlined" style={{ backgroundColor: `${theme.colors.primary}10` }}>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                <Ionicons name="ticket-outline" size={18} color={theme.colors.primary} />
                <Caption color={theme.colors.primary} style={{ flex: 1 }}>
                  Tickets and reservations aren’t included. After creating the trip you can attach your ticket PDFs or booking
                  emails to each itinerary item so everything stays organized.
                </Caption>
              </View>
            </Card>

            {/* Creation Progress */}
            {loading && creationProgress && (
              <Card variant="outlined" style={{ borderColor: theme.colors.primary }}>
                <View style={{ gap: theme.spacing(1.5) }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(2) }}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                    <View style={{ flex: 1 }}>
                      <Body style={{ fontWeight: '600' }}>{creationProgress.stage}</Body>
                      <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                        {Math.round(creationProgress.progress)}% complete
                      </Caption>
                    </View>
                  </View>
                  {/* Progress Bar */}
                  <View
                    style={{
                      height: 6,
                      backgroundColor: theme.colors.border,
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <View
                      style={{
                        height: '100%',
                        width: `${creationProgress.progress}%`,
                        backgroundColor: theme.colors.primary,
                        borderRadius: 3,
                      }}
                    />
                  </View>
                </View>
              </Card>
            )}

            {/* Error Message */}
            {error && !loading && (
              <Card variant="outlined" style={{ backgroundColor: `${theme.colors.error}10` }}>
                <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                  <Ionicons name="alert-circle-outline" size={20} color={theme.colors.error} />
                  <Body color={theme.colors.error}>{error}</Body>
                </View>
              </Card>
            )}
          </View>
        </ScrollView>

        {/* Bottom Action Bar */}
        <View
          style={{
            padding: theme.spacing(2),
            paddingBottom: Math.max(insets.bottom, theme.spacing(2)),
            borderTopWidth: 1,
            borderTopColor: theme.colors.borderMuted,
            backgroundColor: theme.colors.surface,
            gap: theme.spacing(1.5),
          }}
        >
          <Button
            label="Create Trip"
            onPress={handleConfirm}
            disabled={loading || !startDate}
            loading={loading}
          />
          <Button
            label="Cancel"
            variant="ghost"
            onPress={() => onClose('user')}
            disabled={loading}
          />
        </View>

        {/* Success Animation Overlay */}
        <SuccessAnimation
          visible={showSuccess}
          message={`🎉 Trip created!\n${customName.trim() || template?.title || 'Your adventure'} is ready to explore.`}
          onComplete={() => {
            if (successTimeoutRef.current) {
              clearTimeout(successTimeoutRef.current);
              successTimeoutRef.current = null;
            }
            setShowSuccess(false);
            setCreationProgress(null);
            onClose('success');
          }}
        />
        {showYearPicker && (
          <Modal transparent animationType="fade" onRequestClose={() => setShowYearPicker(false)}>
            <Pressable 
              style={{
                flex: 1,
                backgroundColor: 'rgba(0,0,0,0.45)',
                justifyContent: 'center',
                padding: theme.spacing(2),
              }}
              onPress={() => setShowYearPicker(false)}
            >
              <Pressable onPress={(e) => e.stopPropagation()} style={{ alignItems: 'center' }}>
                <Card
                  variant="outlined"
                  style={{
                    padding: theme.spacing(2),
                    backgroundColor: theme.colors.surface,
                    width: '100%',
                    maxWidth: 380,
                    maxHeight: 420,
                    borderRadius: theme.radii.lg,
                    ...theme.shadows.level2,
                  }}
                >
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(1) }}>
                    <SubHeading>Jump to year</SubHeading>
                    <Pressable onPress={() => setShowYearPicker(false)}>
                      <Ionicons name="close" size={20} color={theme.colors.text} />
                    </Pressable>
                  </View>
                  <ScrollView showsVerticalScrollIndicator={true} nestedScrollEnabled={true}>
                    {availableYears.map((year) => {
                      const isActive = year === calendarMonth.getFullYear();
                      return (
                        <Pressable
                          key={year}
                          onPress={() => handleYearSelect(year)}
                          style={({ pressed }) => ({
                            paddingVertical: theme.spacing(0.75),
                            paddingHorizontal: theme.spacing(1),
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderRadius: theme.radii.md,
                            backgroundColor: pressed ? theme.colors.borderMuted + '40' : 'transparent',
                          })}
                          accessibilityRole="button"
                          accessibilityState={{ selected: isActive }}
                          accessibilityLabel={`Select year ${year}`}
                        >
                          <Body style={{ fontWeight: isActive ? '600' : '400' }}>{year}</Body>
                          {isActive && <Ionicons name="checkmark" size={18} color={theme.colors.primary} />}
                        </Pressable>
                      );
                    })}
                  </ScrollView>
                </Card>
              </Pressable>
            </Pressable>
          </Modal>
        )}
      </View>
  );

  if (embedded) {
    return content;
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => onClose('user')}
      onShow={handleModalOpen}
      onDismiss={onDismiss}
    >
      {content}
    </Modal>
  );
}

